import type { FeatureFlags } from '@packages/contexts/feature-flags';

import { authActions, AuthStatus, touchAuthSession, useAuthDispatch, useAuthState } from '@packages/contexts/auth';
import { BmisProvider } from '@packages/contexts/bmis';
import { CidCvnProvider } from '@packages/contexts/cid-cvn';
import { ContentSettingsProvider } from '@packages/contexts/content-settings';
import { CvtProvider } from '@packages/contexts/cvt';
import { DmsProvider } from '@packages/contexts/dms';
import { useEscalationModal } from '@packages/contexts/escalations';
import { useInvestigationModal } from '@packages/contexts/investigations';
import { PermissionsProvider } from '@packages/contexts/permissions';
import { CvtDiagnosticUploadsProvider } from '@packages/contexts/qmr-cvt-diagnostic';
import { QmrProvider } from '@packages/contexts/qmrs';
import { getLogger } from '@packages/core/logging';
import { WebStorageKeys } from '@packages/core/storage';
import { type Account } from '@packages/models/api';
import { IdleWarningPopup, SessionTimeoutPopup } from '@packages/ui/shared/popups';
import { AdministrationRouter } from '@web/administration/administration-router';
import { FailCodeCsvExport } from '@web/administration/components/fail-code-teams-csv-export';
import { BmisRouter } from '@web/bmis/bmis-router';
import { CidCvnCheck } from '@web/cid-cvn/view/cid-cvn-check';
import { CidCvnReport } from '@web/cid-cvn/view/cid-cvn-report';
import EmailNotificationsModal from '@web/components/email-notifications-modal';
import Header from '@web/components/header';
import Navigation from '@web/components/navigation';
import PrivateRoute from '@web/components/private-route';
import { StisAccessGate } from '@web/components/stis-access-gate';
import constants from '@web/config/constants';
import { useContentOverlayState } from '@web/core/hooks/use-content-overlay';
import { useIdleTimeout } from '@web/core/hooks/use-idle-timeout';
import CVT from '@web/cvt/views/cvt';
import { EscalationModal } from '@web/escalations/components';
import { EscalationDetail, Escalations } from '@web/escalations/views';
import { FeedbackBrowser, FeedbackDetail } from '@web/feedback/views';
import { InvestigationModal } from '@web/investigations/components/investigation-modal';
import InvestigationDetails from '@web/investigations/views/investigation-details';
import Investigations from '@web/investigations/views/investigations';
import AdvancedSearch from '@web/qmr/views/advanced-search';
import AdvancedSearchResults from '@web/qmr/views/advanced-search-results';
import AdvancedSearchSaved from '@web/qmr/views/advanced-search-saved';
import QmrDetail from '@web/qmr/views/qmr-detail';
import QmrDashboard from '@web/qmr/views/qmrs-dashboard';
import { ServiceDiagnostics } from '@web/service-diagnostics/service-diagnostics';
import { TroubleshootingSearch } from '@web/service-diagnostics/troubleshooting-search';
import {
    authService,
    authTokenService,
    cidCvnService,
    contentSettingsService,
    cvtService,
    googleAnalyticsService,
    httpClient,
    permissionsService,
    pushNotificationsService,
    storageService,
    vinSessionService,
} from '@web/services/singletons';
import { Ssm5ConnectionProvider } from '@web/ssm5/context/ssm5-connection-provider';
import { Ssm5Diagnostics } from '@web/ssm5/views/ssm5-diagnostics';
import { CaseManagementView } from '@web/techline/case-management-view';
import { DashboardView } from '@web/techline/dashboard/v1/dashboard-view';
import { DashboardV2View } from '@web/techline/dashboard/v2/dashboard-v2-view';
import { UserArticles } from '@web/userarticles/user-articles';
import { AuthLoading } from '@web/views/auth-loading';
import FallbackStis from '@web/views/fallback-stis';
import NoMatch from '@web/views/no-match';
import Overview from '@web/views/overview';
import PilotOnboarding from '@web/views/pilot-onboarding';
import QmrCsvExport from '@web/views/qmr-csv-export';
import Qmrs from '@web/views/qmrs';
import Reports from '@web/views/reports';
import { ReportsDownload } from '@web/views/reports-download';
import Settings from '@web/views/settings';
import { ContactUs } from '@web/views/techshareLandingPage/contact-us';
import { EmergencyGuides } from '@web/views/techshareLandingPage/emergency-guides';
import { FAQ } from '@web/views/techshareLandingPage/faq';
import { LandingPageLayout } from '@web/views/techshareLandingPage/landing-page-layout';
import { PrivacyPolicy } from '@web/views/techshareLandingPage/privacy-policy';
import { SiteLinkLayout } from '@web/views/techshareLandingPage/site-link-layout';
import { SpecialTools } from '@web/views/techshareLandingPage/special-tools';
import { TermsAndConditionsAndPrivacy } from '@web/views/techshareLandingPage/terms-conditions';
import UserOnboarding from '@web/views/user-onboarding';
import { useVinSessionAnalytics } from '@web/vin-sessions/analytics/hooks/use-vin-session-analytics';
import { CampaignsRouter } from '@web/vin-sessions/components/campaigns/campaigns-router';
import { DmsVinSync } from '@web/vin-sessions/components/dms/components/dms-vin-sync';
import { DmsRouter } from '@web/vin-sessions/components/dms/dms-router';
import DocsQuerySearch from '@web/vin-sessions/components/docs-query-search';
import { CdnRedirectHandler } from '@web/vin-sessions/components/stis-publications/cdn-redirect-handler';
import {
    usePublicationsDispatch,
    usePublicationsState,
} from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { publicationsActions } from '@web/vin-sessions/components/stis-publications/context/publications.state';
import { PdfHighlighterNew } from '@web/vin-sessions/components/stis-publications/pdf-highlighter-new';
import {
    clearListFilters,
    isStisFilterPath,
} from '@web/vin-sessions/components/stis-publications/publication-list-filters.storage';
import { SsmRedirectHandler } from '@web/vin-sessions/components/stis-publications/ssm-redirect-handler';
import { StisPublications } from '@web/vin-sessions/components/stis-publications/stis-publications';
import { ToastSuccessMessage } from '@web/vin-sessions/components/toast-success';
import VinSessionBanner from '@web/vin-sessions/components/vin-session-banner';
import { useVinSessionDispatch, useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { vinSessionActions } from '@web/vin-sessions/context/vin-sessions.state';
import { closeVinSession, handleVinSessionError, openVinSession } from '@web/vin-sessions/utils/vinSessionUtils';
import { VinSessionDashboard } from '@web/vin-sessions/views/vin-session-dashboard';
import { type ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Navigate, Route, Routes, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { CvtReport } from '../../cvt/views/cvt-report';
import { ModuleName } from './module-name';

const pushNotificationsLogger = getLogger('push-notifications');
const logger = getLogger('web');

const IDLE_LOGOUT_CLAIM_WAIT_MS = 100;

async function claimIdleLogout() {
    const claim = crypto.randomUUID();
    await storageService.setItem(WebStorageKeys.IdleLogoutClaim, claim);
    await new Promise((resolve) => setTimeout(resolve, IDLE_LOGOUT_CLAIM_WAIT_MS));

    return (await storageService.getItem(WebStorageKeys.IdleLogoutClaim)) === claim;
}

export type AppRoute = {
    fallback?: ComponentType;
    /** Hides the route behind a feature flag. When the flag is off the route redirects to the dashboard. */
    featureFlag?: keyof typeof FeatureFlags;
    hideOnlyNav?: boolean;
    main?: ComponentType;
    path: string;
    private?: boolean;
    redirectTo?: string;
    routeGuard?: (account: Account | null, canAccessStis?: boolean) => boolean;
    useChildRouter?: boolean | undefined;
    moduleName?: ModuleName;
};

const canViewQmrs = (account: Account | null) => Boolean(account?.systemCapabilities?.canViewQualityAssurance);
const canViewFeedback = (account: Account | null) => Boolean(account?.feedbackCapabilities?.giveOrRequestFeedback);
const canViewTechline = (account: Account | null) => Boolean(account?.systemCapabilities?.canViewTechline);

export const routes: AppRoute[] = [
    {
        path: '/auth',
        private: false,
        main: AuthLoading,
    },
    {
        path: '/',
        private: true,
        main: Overview,
        moduleName: ModuleName.Dashboard,
    },
    {
        path: '/vin/:vin',
        private: true,
        main: () => {
            return (
                <BmisProvider>
                    <VinSessionDashboard />
                </BmisProvider>
            );
        },
        redirectTo: '/',
        moduleName: ModuleName.VinSession,
    },
    {
        path: '/vin/:vin/stis/*',
        private: true,
        main: () => (
            <StisAccessGate>
                <StisPublications />
            </StisAccessGate>
        ),
        redirectTo: '/',
        moduleName: ModuleName.Stis,
    },
    {
        path: '/stis/*',
        private: true,
        main: () => (
            <StisAccessGate>
                <StisPublications />
            </StisAccessGate>
        ),
        redirectTo: '/',
        routeGuard: (account, canAccessStis) =>
            Boolean(account?.docsCapabilities?.canViewSTISPublications) || (canAccessStis ?? false),
        fallback: FallbackStis,
        moduleName: ModuleName.Stis,
    },
    {
        path: '/service-diagnostics',
        private: true,
        main: () => (
            <StisAccessGate>
                <ServiceDiagnostics />
            </StisAccessGate>
        ),
        redirectTo: '/',
        routeGuard: (account, canAccessStis) =>
            Boolean(account?.docsCapabilities?.canViewSTISPublications) || (canAccessStis ?? false),
        fallback: FallbackStis,
        moduleName: ModuleName.StisServiceDiagnostics,
    },
    {
        path: '/service-diagnostics/troubleshooting-search',
        private: true,
        main: () => (
            <StisAccessGate>
                <TroubleshootingSearch />
            </StisAccessGate>
        ),
        redirectTo: '/',
        routeGuard: (account, canAccessStis) =>
            Boolean(account?.docsCapabilities?.canViewSTISPublications) || (canAccessStis ?? false),
        fallback: FallbackStis,
        moduleName: ModuleName.StisTroubleshootingSearch,
    },
    // Service Diagnostics moved out from under /stis (TECHSHARE-9153). Both static paths
    // outrank the /stis/* wildcard, so existing links and bookmarks still land on the page.
    {
        path: '/stis/service-diagnostics',
        redirectTo: '/service-diagnostics',
    },
    {
        path: '/stis/service-diagnostics/troubleshooting-search',
        redirectTo: '/service-diagnostics/troubleshooting-search',
    },
    {
        path: '/docs/',
        private: true,
        main: DocsQuerySearch,
        moduleName: ModuleName.Docs,
    },
    {
        path: '/onboard',
        private: true,
        main: UserOnboarding,
        moduleName: ModuleName.Onboard,
    },
    {
        path: '/stis/onboard',
        private: true,
        main: () => (
            <StisAccessGate>
                <PilotOnboarding />
            </StisAccessGate>
        ),
        moduleName: ModuleName.StisOnboard,
    },
    {
        path: '/preview/*',
        private: true,
        main: PdfHighlighterNew,
        moduleName: ModuleName.Preview,
    },
    {
        path: '/qmrs',
        private: true,
        main: Qmrs,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Qmrs,
    },
    {
        path: '/vin/:id/qmrs',
        private: true,
        main: Qmrs,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Qmrs,
    },
    {
        path: '/vin/:id/nqrs',
        private: true,
        main: () => {
            return <Qmrs showNQQmrs={true} />;
        },
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Nqrs,
    },
    {
        path: '/vin/:id/escalations',
        private: true,
        main: Escalations,
        moduleName: ModuleName.Escalations,
    },
    {
        path: '/vin/:id/investigations',
        private: true,
        main: Investigations,
        routeGuard: (account) => Boolean(account?.investigationCapabilities?.viewInvestigations),
        moduleName: ModuleName.Investigations,
    },
    {
        path: '/vin/:id/techline',
        private: true,
        main: () => {
            return (
                <ContentSettingsProvider service={contentSettingsService}>
                    <DashboardView />
                </ContentSettingsProvider>
            );
        },
        moduleName: ModuleName.Techline,
    },
    {
        path: '/nqrs',
        private: true,
        main: () => {
            return <Qmrs showNQQmrs={true} />;
        },
        moduleName: ModuleName.Nqrs,
    },
    {
        path: '/vin/:vin/bmis/*',
        private: true,
        main: () => {
            return <BmisRouter />;
        },
        useChildRouter: true,
        moduleName: ModuleName.Bmis,
    },
    {
        path: '/vin/:vin/repair-orders/*',
        private: true,
        main: () => {
            return <DmsRouter />;
        },
        useChildRouter: true,
        moduleName: ModuleName.RepairOrders,
    },
    {
        path: '/vin/:vin/campaigns/*',
        private: true,
        main: () => {
            return <CampaignsRouter />;
        },
        useChildRouter: true,
        moduleName: ModuleName.Campaigns,
    },
    {
        path: '/qmrs/dashboard',
        private: true,
        main: QmrDashboard,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsDashboard,
    },
    {
        path: '/qmrs/advanced-search/search',
        private: true,
        main: AdvancedSearch,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsAdvancedSearch,
    },
    {
        path: '/qmrs/advanced-search/saved',
        private: true,
        main: AdvancedSearchSaved,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsAdvancedSearch,
    },
    {
        path: '/qmrs/advanced-search/saved/:searchId',
        private: true,
        main: AdvancedSearchResults,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsAdvancedSearch,
    },
    {
        path: '/qmrs/advanced-search/:searchId',
        private: true,
        main: AdvancedSearchResults,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsAdvancedSearch,
    },
    {
        path: '/qmrs/:displayIdentifier',
        private: true,
        main: () => {
            return (
                <QmrProvider>
                    <QmrDetail />
                </QmrProvider>
            );
        },
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Qmrs,
    },
    {
        path: '/qmrs/:displayIdentifier/edit',
        private: true,
        main: () => {
            return (
                <QmrProvider>
                    <QmrDetail />
                </QmrProvider>
            );
        },
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Qmrs,
    },
    {
        path: '/nqrs/:displayIdentifier',
        private: true,
        main: () => {
            return (
                <QmrProvider>
                    <QmrDetail />
                </QmrProvider>
            );
        },
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Nqrs,
    },
    {
        path: '/nqrs/:displayIdentifier/edit',
        private: true,
        main: () => {
            return (
                <QmrProvider>
                    <QmrDetail />
                </QmrProvider>
            );
        },
        routeGuard: canViewQmrs,
        moduleName: ModuleName.Nqrs,
    },
    {
        path: '/qmrs/csv-export/:exportId',
        private: true,
        main: QmrCsvExport,
        routeGuard: canViewQmrs,
        moduleName: ModuleName.QmrsCsvExport,
    },
    {
        path: '/techline/:caseNumber',
        private: true,
        main: CaseManagementView,
        routeGuard: canViewTechline,
        moduleName: ModuleName.Techline,
    },
    {
        path: '/vin/:id/techline/:caseNumber/cid-cvn-worksheet',
        private: true,
        main: CaseManagementView,
        routeGuard: canViewTechline,
        moduleName: ModuleName.Techline,
    },
    {
        path: '/techline',
        redirectTo: '/techline/dashboard',
    },
    {
        path: '/techline/dashboard',
        private: true,
        main: () => {
            return (
                <ContentSettingsProvider service={contentSettingsService}>
                    <DashboardView />
                </ContentSettingsProvider>
            );
        },
        routeGuard: canViewTechline,
        moduleName: ModuleName.Techline,
    },

    // To Do: As part of epic "Techline | Dashboards" (TECHSHARE-2210),
    // update this path from techline/dashboardv2 to /techline/cases and use <DashboardView> instead of <DashboardV2View>.

    {
        path: '/techline/dashboardv2',
        private: true,
        main: () => {
            return (
                <ContentSettingsProvider service={contentSettingsService}>
                    <DashboardV2View />
                </ContentSettingsProvider>
            );
        },
        routeGuard: canViewTechline,
        moduleName: ModuleName.Techline,
    },
    {
        path: '/feedback',
        private: true,
        main: FeedbackBrowser,
        routeGuard: canViewFeedback,
        moduleName: ModuleName.Feedback,
    },
    {
        path: '/feedback/:feedbackSessionId',
        private: true,
        main: FeedbackDetail,
        routeGuard: canViewFeedback,
        moduleName: ModuleName.Feedback,
    },
    {
        path: '/feedback/entity/:feedbackCreationUrlPath',
        private: true,
        main: FeedbackDetail,
        routeGuard: canViewFeedback,
        moduleName: ModuleName.Feedback,
    },
    {
        path: '/investigations',
        private: true,
        main: Investigations,
        routeGuard: (account) => Boolean(account?.investigationCapabilities?.viewInvestigations),
        moduleName: ModuleName.Investigations,
    },
    {
        path: '/investigations/:investigationId',
        private: true,
        main: InvestigationDetails,
        routeGuard: (account) => Boolean(account?.investigationCapabilities?.viewInvestigations),
        moduleName: ModuleName.Investigations,
    },
    {
        path: '/escalations',
        private: true,
        main: Escalations,
        moduleName: ModuleName.Escalations,
    },
    {
        path: '/escalations/:trReplyId',
        private: true,
        main: EscalationDetail,
        moduleName: ModuleName.Escalations,
    },
    {
        path: '/reports',
        private: true,
        main: Reports,
        moduleName: ModuleName.Reports,
    },
    {
        path: '/reports/:reportId',
        private: true,
        main: ReportsDownload,
        moduleName: ModuleName.Reports,
    },
    {
        path: '/settings',
        private: true,
        main: Settings,
        moduleName: ModuleName.Settings,
    },
    {
        path: '/administration/*',
        private: true,
        main: () => {
            return <AdministrationRouter />;
        },
        useChildRouter: true,
        moduleName: ModuleName.Administration,
    },
    {
        path: '/failCode/csv-export/:exportId',
        private: true,
        main: FailCodeCsvExport,
        moduleName: ModuleName.FailCodeCsvExport,
    },
    {
        path: '/user-articles',
        private: false,
        main: UserArticles,
        routeGuard: (account) => Boolean(account?.systemCapabilities?.viewDocumentation),
        moduleName: ModuleName.UserArticles,
    },
    {
        path: '/user-articles/search',
        private: false,
        main: UserArticles,
        routeGuard: (account) => Boolean(account?.systemCapabilities?.viewDocumentation),
        moduleName: ModuleName.UserArticles,
    },
    {
        path: '/user-articles/edit/:id',
        private: false,
        main: UserArticles,
        routeGuard: (account) => Boolean(account?.systemCapabilities?.manageDocumentation),
        moduleName: ModuleName.UserArticles,
    },
    {
        path: '/user-articles/new',
        private: false,
        main: UserArticles,
        routeGuard: (account) => Boolean(account?.systemCapabilities?.manageDocumentation),
        moduleName: ModuleName.UserArticles,
    },
    {
        path: '/user-articles/:id',
        private: false,
        main: UserArticles,
        routeGuard: (account) => Boolean(account?.systemCapabilities?.manageDocumentation),
        moduleName: ModuleName.UserArticles,
    },
    {
        path: '/vin/:vin/cid-cvn-check',
        private: true,
        main: CidCvnCheck,
        routeGuard: (account) => Boolean(account?.cidCvnCapabilities?.canViewCIDCVNList),
        moduleName: ModuleName.CidCvnCheck,
    },
    {
        path: '/vin/:vin/cid-cvn-check/:id',
        private: true,
        main: CidCvnReport,
        routeGuard: (account) => Boolean(account?.cidCvnCapabilities?.canViewCIDCVNResults),
        moduleName: ModuleName.CidCvnCheck,
    },
    {
        path: '/ssm5',
        private: true,
        main: Ssm5Diagnostics,
        featureFlag: 'isSsm5DiagnosticsFeatureEnabled',
        moduleName: ModuleName.Ssm5,
    },
    {
        path: '/vin/:vin/ssm5/*',
        useChildRouter: true,
        private: true,
        main: Ssm5Diagnostics,
        featureFlag: 'isSsm5DiagnosticsFeatureEnabled',
        moduleName: ModuleName.Ssm5,
    },
    {
        path: '/vin/:vin/cvt',
        private: false,
        main: CVT,
        moduleName: ModuleName.Cvt,
    },
    {
        path: '/vin/:vin/cvt/:id',
        private: false,
        main: CvtReport,
        moduleName: ModuleName.Cvt,
    },
    {
        path: '/app/privacy-policy',
        private: false,
        main: () => (
            <SiteLinkLayout>
                <PrivacyPolicy />
            </SiteLinkLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.PrivacyPolicy,
    },
    {
        path: '/app/terms-conditions',
        private: false,
        main: () => (
            <SiteLinkLayout>
                <TermsAndConditionsAndPrivacy />
            </SiteLinkLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.TermsConditions,
    },
    {
        path: '/app/faq',
        private: false,
        main: () => (
            <SiteLinkLayout>
                <FAQ />
            </SiteLinkLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.Faq,
    },
    {
        path: '/app/contact_us',
        private: false,
        main: () => (
            <SiteLinkLayout>
                <ContactUs />
            </SiteLinkLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.ContactUs,
    },
    {
        path: '/app/emergency-guides',
        private: false,
        main: () => (
            <LandingPageLayout>
                <EmergencyGuides />
            </LandingPageLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.EmergencyGuides,
    },
    {
        path: '/app/diagnostic-tool-info',
        private: false,
        main: () => (
            <SiteLinkLayout>
                <SpecialTools />
            </SiteLinkLayout>
        ),
        hideOnlyNav: true,
        moduleName: ModuleName.DiagnosticToolInfo,
    },
    {
        path: '/public/privacy-policy',
        redirectTo: '/app/privacy-policy',
        moduleName: ModuleName.PrivacyPolicy,
    },
    {
        path: '/public/terms-conditions',
        redirectTo: '/app/terms-conditions',
        moduleName: ModuleName.TermsConditions,
    },
    {
        path: '/public/faq',
        redirectTo: '/app/faq',
        moduleName: ModuleName.Faq,
    },
    {
        path: '/public/contact_us',
        redirectTo: '/app/contact_us',
        moduleName: ModuleName.ContactUs,
    },
    {
        path: '/public/emergency-guides',
        redirectTo: '/app/emergency-guides',
        moduleName: ModuleName.EmergencyGuides,
    },
    {
        path: '/public/diagnostic-tool-info',
        redirectTo: '/app/diagnostic-tool-info',
        moduleName: ModuleName.DiagnosticToolInfo,
    },
    {
        path: '*',
        private: false,
        main: NoMatch,
        moduleName: ModuleName.NotFound,
    },
];

const useAppNavigatorStyles = createUseStyles({
    appNavigator: {
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
    },
    contentAreaOverlay: ({ showOverlay, draggable }: { showOverlay: boolean; draggable: boolean }) => ({
        zIndex: showOverlay ? 10 : -10,
        backgroundColor: showOverlay ? 'rgba(0,0,0,0.65)' : 'transparent',
        position: 'absolute',
        top: constants.headerHeight,
        bottom: 0,
        right: showOverlay ? 0 : draggable ? 0 : -390,
        left: 0,
        transition: 'right 0.5s, background-color 0.5s',
        maxWidth: '100%',
    }),
    contentArea: {
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'row',
        backgroundColor: 'white',
    },
    routeSidebarOuter: {
        display: 'flex',
    },
    routeMainOuter: {
        flex: 1,
        overflow: 'hidden',
        overflowY: 'scroll',
    },
    '@media(max-width: 600px)': {
        contentAreaOverlay: ({ showOverlay, draggable }: { showOverlay: boolean; draggable: boolean }) => ({
            right: showOverlay ? 0 : draggable ? 0 : 0,
        }),
    },
    '@media(min-width: 768px) and (max-width: 1024px)': {
        routeMainOuter: {
            scrollbarWidth: 'none',
        },
    },
});

export function AppNavigator() {
    const { account } = useAuthState();
    const { status: authStatus } = useAuthState();
    const authDispatch = useAuthDispatch();
    const isProcessingAuthRedirect = useMatch('/auth');
    const pspdfNavigateRouteMatch = useMatch('/preview/*');
    const [navIsOpen, setNavIsOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const currentRoute = useMemo(() => {
        return routes.find((route) => {
            if (route.path.includes('*')) {
                const basePath = route.path.replace('/*', '');
                return location.pathname.startsWith(basePath);
            }
            return location.pathname === route.path;
        });
    }, [location.pathname]);

    const shouldHideOnlyNav = currentRoute?.hideOnlyNav ?? false;
    const { showOverlay, dismissAllOverlays, draggable } = useContentOverlayState();
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [showIdleWarningModal, setShowIdleWarningModal] = useState(false);
    const classes = useAppNavigatorStyles({ showOverlay, draggable });
    const pushNotificationsInitialized = useRef(false);
    const hasVinSessionAbandonFiredRef = useRef(false);
    const { show, hideModal } = useInvestigationModal();
    const { show: showEscalationModal, hideModal: hideEscalationModal } = useEscalationModal();
    const { isVinLoading, selectedVin, title, vinBannerOpen, hasVinDataFetched, sessionLifecycle } =
        useVinSessionState();
    const [vinSessionLoader, setVinSessionLoader] = useState<boolean>(false);
    const dispatch = useVinSessionDispatch();
    const { toast } = usePublicationsState();
    const publicationDispatch = usePublicationsDispatch();
    const { trackVinSessionStartEvent, trackVinSessionEndEvent, trackVinSessionAbandonEvent } =
        useVinSessionAnalytics();

    const handleNotAuthenticated = useMemo(() => {
        let isHandlingNotAuthRedirect = false;

        return () => {
            if (isHandlingNotAuthRedirect) {
                return;
            }

            isHandlingNotAuthRedirect = true;

            googleAnalyticsService.resetUser();
            authDispatch(authActions.resetState());
            navigate('/', { replace: true });
        };
    }, [authDispatch, navigate]);

    // Navigation Path Logic
    const fullNavigationPath = useMemo(() => location.pathname, [location.pathname]);
    const navigationPathParts = useMemo(() => fullNavigationPath.split('/'), [fullNavigationPath]);

    // Account Logic
    const accountId = account?.accountId || null;

    // Vin Path Logic
    const isVinSessionActive = useMatch('/vin/*');
    const vinString = useMemo(
        () => (isVinSessionActive ? navigationPathParts[2] : null),
        [isVinSessionActive, navigationPathParts]
    );

    const fetchVin = (vin: string) => {
        async function getVin(vin: string) {
            setVinSessionLoader(true);
            dispatch(
                vinSessionActions.setIsVinLoading({
                    isVinLoading: true,
                })
            );
            try {
                const response = await vinSessionService.getVinData({
                    fetchVinParams: {
                        vin: vin,
                    },
                    ignoreCache: true,
                });
                if (response.success) {
                    if (!response.data || JSON.stringify(response.data) === '[]' || response.data.length === 0) {
                        setVinSessionLoader(false);
                        navigate('/');
                        // An unknown VIN is a user input problem, not an app failure.
                        logger.warn(`No VIN record found for [${vin}], returning to the dashboard`, { vin });
                        dispatch(
                            vinSessionActions.setIsVinLoading({
                                isVinLoading: false,
                            })
                        );
                        return;
                    }
                    dispatch(
                        vinSessionActions.setSelectedVin({
                            selectedVin: response.data[0],
                        })
                    );
                    dispatch(vinSessionActions.setTitle({ title: response.data[0].vinRecord.vin }));
                    dispatch(vinSessionActions.setVinBannerOpen({ vinBannerOpen: true }));
                    dispatch(vinSessionActions.setHasVinDataFetched());
                    dispatch(vinSessionActions.setSessionStarted({ startedAt: Date.now() }));
                    trackVinSessionStartEvent(response.data[0].vinRecord);
                }
            } catch (error) {
                navigate('/');
                setVinSessionLoader(false);
                dispatch(vinSessionActions.setIsVinLoading({ isVinLoading: false }));
                dispatch(vinSessionActions.setSelectedVin({ selectedVin: undefined }));
                handleVinSessionError(`Vin Fetching Error: No Record Found; an unknown error occurred`, error);
                return;
            }
            openVinSession(vin, account?.accountId, setVinSessionLoader, selectedVin);
            setVinSessionLoader(false);
            dispatch(
                vinSessionActions.setIsVinLoading({
                    isVinLoading: false,
                })
            );
        }

        if (vin.length < 8) {
            navigate('/');
            setVinSessionLoader(false);
            const error = new Error('Invalid VIN');
            handleVinSessionError(`Vin Fetching Error: ${error.message}`, error);
            return;
        }
        getVin(vin);
    };

    // Track the latest account so handleTokenUpdate can diff against it without being
    // re-created (which would re-register the token-update handler on every account change).
    const latestAccountRef = useRef(account);
    latestAccountRef.current = account;

    const handleTokenUpdate = useCallback(
        (token: string) => {
            authDispatch(authActions.updateToken({ token }));

            touchAuthSession({ storageService });

            // A refreshed token carries an account snapshot; re-hydrate the profile so
            // capability/subscription changes propagate without a re-login. Only dispatch when
            // the account actually changed — `getAccountFromToken` returns a fresh object on
            // every call, and re-setting an equal-but-new reference churns `account` app-wide,
            // re-firing account-keyed effects (e.g. notification polling) that refresh the token
            // again, creating a request loop (most visible locally where tokens refresh often).
            const nextAccount = authTokenService.getAccountFromToken(token);
            if (nextAccount && JSON.stringify(nextAccount) !== JSON.stringify(latestAccountRef.current)) {
                authDispatch(authActions.setAccount({ account: nextAccount }));
            }
        },
        [authDispatch]
    );

    const handleIdleTimeout = useCallback(async () => {
        setShowIdleWarningModal(false);

        if (!(await claimIdleLogout())) {
            return;
        }

        try {
            const response = await authService.logout();
            if (response.success && response.data) {
                const html = await (response.data as any);
                const blob = new Blob([html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                googleAnalyticsService.resetUser();
                storageService.removeItem(WebStorageKeys.AccessToken);
                authDispatch(authActions.resetState());
                window.open(url, '_self');
                return;
            }
        } catch (error) {
            logger.error('Failed to complete remote logout during idle timeout', { error });
        }
        // Remote logout failed or returned no redirect — still clear local session
        googleAnalyticsService.resetUser();
        storageService.removeItem(WebStorageKeys.AccessToken);
        authDispatch(authActions.resetState());
    }, [authDispatch]);

    const handleIdleWarning = useCallback(() => {
        setShowIdleWarningModal(true);
    }, []);

    const handleActiveInAnotherTab = useCallback(() => {
        setShowIdleWarningModal(false);
    }, []);

    const { reset: resetIdleTimer } = useIdleTimeout({
        onWarning: handleIdleWarning,
        onTimeout: handleIdleTimeout,
        onActive: handleActiveInAnotherTab,
        activityStorageKey: storageService.keyFor(WebStorageKeys.LastActivityAt),
        enabled: authStatus === AuthStatus.Authenticated,
    });

    useEffect(() => {
        if (sessionLifecycle?.startedAt) {
            hasVinSessionAbandonFiredRef.current = false;
        }
    }, [sessionLifecycle?.startedAt]);

    useEffect(() => {
        if (!selectedVin || !sessionLifecycle || sessionLifecycle.hasEnded) {
            return;
        }

        const handlePageHide = () => {
            if (hasVinSessionAbandonFiredRef.current) {
                return;
            }
            hasVinSessionAbandonFiredRef.current = true;
            trackVinSessionAbandonEvent();
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => {
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [selectedVin, sessionLifecycle, trackVinSessionAbandonEvent]);

    useEffect(() => {
        if (authStatus === AuthStatus.Authenticated) {
            httpClient.registerSessionTimeoutHandler(() => {
                setShowTimeoutModal(true);
            });

            httpClient.registerNotAuthenticatedHandler(handleNotAuthenticated);
            httpClient.registerTokenUpdateHandler(handleTokenUpdate);

            if (!pushNotificationsInitialized.current) {
                pushNotificationsInitialized.current = true;
                pushNotificationsLogger.info('Initializing the push notification service (permission/token/device)');
                pushNotificationsService.init();
            }
        }

        const handleWake = () => {
            if (authStatus !== AuthStatus.Authenticated || document.visibilityState === 'hidden') {
                return;
            }
            httpClient.checkStoredToken();
        };

        const handleTokenStorage = (event: StorageEvent) => {
            if (
                authStatus !== AuthStatus.Authenticated ||
                event.key !== storageService.keyFor(WebStorageKeys.AccessToken)
            ) {
                return;
            }

            if (event.newValue === null) {
                handleNotAuthenticated();
                return;
            }

            setShowTimeoutModal(false);
            handleTokenUpdate(JSON.parse(event.newValue));
            httpClient.checkStoredToken();
        };

        window.addEventListener('focus', handleWake);
        window.addEventListener('online', handleWake);
        document.addEventListener('visibilitychange', handleWake);
        window.addEventListener('storage', handleTokenStorage);

        return () => {
            window.removeEventListener('focus', handleWake);
            window.removeEventListener('online', handleWake);
            document.removeEventListener('visibilitychange', handleWake);
            window.removeEventListener('storage', handleTokenStorage);
            if (authStatus === AuthStatus.Unauthenticated) {
                pushNotificationsLogger.info('Terminating the push notification service');
                pushNotificationsInitialized.current = false;
                pushNotificationsService.terminate();
            }
        };
    }, [authDispatch, authStatus, handleNotAuthenticated, handleTokenUpdate]);

    useEffect(() => {
        if (!isVinSessionActive && !isVinLoading) {
            if (selectedVin) {
                trackVinSessionEndEvent('Navigate Away');
            }
            dispatch(vinSessionActions.resetState());
        } else {
            // selectedVin is intentionally read from closure but not listed as a dep.
            // Adding it would cause the effect to re-fire after every successful fetch
            // (before hasVinDataFetched is committed) and trigger duplicate API calls.
            // vinString already re-triggers the effect when the URL changes to a new VIN.
            const hasVinChanged = selectedVin && vinString && selectedVin.vinRecord.vin !== vinString;

            if (accountId && vinString && !isVinLoading && (!hasVinDataFetched || hasVinChanged)) {
                if (hasVinChanged) {
                    trackVinSessionEndEvent('Change Vin');
                    dispatch(vinSessionActions.resetState());
                }
                fetchVin(vinString);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, isVinSessionActive, isVinLoading, vinString, hasVinDataFetched]);

    useEffect(() => {
        document.title = title;
    }, [title]);

    useEffect(() => {
        if (account) {
            publicationDispatch(
                publicationsActions.setStisFeedbackPrompt({ stisFeedbackPrompt: account.stisFeedbackPrompt })
            );
        }
    }, [account]);

    useEffect(() => {
        const handleBeforeUnload = (event: any) => {
            closeVinSession(vinString, account?.accountId, setVinSessionLoader, dispatch);
            event.returnValue = 'You are about to close the Vin-Session';
            return 'You are about to close the Vin-Session';
        };

        if (isVinSessionActive) {
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }
    }, [isVinSessionActive]);

    useEffect(() => {
        if (toast.toastMessage.length > 0) {
            setTimeout(() => {
                publicationDispatch(
                    publicationsActions.setToastMessage({
                        toast: {
                            toastMessage: '',
                            iconColor: 'white',
                            iconName: '',
                        },
                    })
                );
            }, 3000);
        }
    }, [toast]);

    useEffect(() => {
        if (location.pathname.startsWith('/stis/diag/')) {
            sessionStorage.setItem('ssm_redirect_path', location.pathname + location.search);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!isStisFilterPath(location.pathname)) {
            clearListFilters();
        }
    }, [location.pathname]);

    // CloudFront redirects an expired-cookie document bookmark back to our app domain
    // at /doc/<docpath> or /techshare-docs/<docpath>. Stash the path so it survives the
    // login round-trip, then CdnRedirectHandler re-resolves it via /cdn-url-by-path.
    useEffect(() => {
        if (
            location.pathname.startsWith('/redirect/stis') ||
            location.pathname.startsWith('/doc/') ||
            location.pathname.startsWith('/techshare-docs/')
        ) {
            logger.info('Redirected back from an expired document link', { path: location.pathname });
            sessionStorage.setItem('cdn_redirect_path', location.pathname + location.search);
        }
    }, [location.pathname, location.search]);

    if (!isProcessingAuthRedirect && authStatus !== AuthStatus.Authenticated) {
        return <AuthLoading />;
    }

    if (
        location.pathname.startsWith('/stis/diag/') ||
        (!isProcessingAuthRedirect && sessionStorage.getItem('ssm_redirect_path'))
    ) {
        return <SsmRedirectHandler />;
    }

    if (
        location.pathname.startsWith('/redirect/stis') ||
        location.pathname.startsWith('/doc/') ||
        location.pathname.startsWith('/techshare-docs/') ||
        (!isProcessingAuthRedirect && sessionStorage.getItem('cdn_redirect_path'))
    ) {
        return <CdnRedirectHandler />;
    }

    return (
        <Ssm5ConnectionProvider>
            <PermissionsProvider permissionsService={permissionsService}>
                <CvtProvider cvtService={cvtService} vin={selectedVin?.vinRecord.vin}>
                    <CvtDiagnosticUploadsProvider>
                        <CidCvnProvider cidCvnService={cidCvnService} vin={selectedVin?.vinRecord.vin}>
                            <DmsProvider>
                                <DmsVinSync />
                                <BmisProvider>
                                    <div className={classes.appNavigator}>
                                        {pspdfNavigateRouteMatch == null && !shouldHideOnlyNav && (
                                            <Header
                                                setVinSessionLoader={setVinSessionLoader}
                                                vinSessionLoader={vinSessionLoader}
                                            />
                                        )}

                                        {vinBannerOpen && <VinSessionBanner />}

                                        <InvestigationModal
                                            onHide={() => {
                                                hideModal();
                                            }}
                                            show={show}
                                        />

                                        <EscalationModal
                                            onHide={() => {
                                                hideEscalationModal();
                                            }}
                                            show={showEscalationModal}
                                        />

                                        <EmailNotificationsModal
                                            onHide={() => {
                                                navigate(location.pathname, {
                                                    ...location,
                                                    state: { recentlyOnboarded: false },
                                                });
                                            }}
                                            show={location.state && location.state.recentlyOnboarded}
                                        />

                                        <SessionTimeoutPopup
                                            onExtendSession={() => {
                                                httpClient
                                                    .forceRefreshToken()
                                                    .then(() => {
                                                        setShowTimeoutModal(false);
                                                    })
                                                    .catch((error) => {
                                                        logger.warn('Failed to extend the session', { error });
                                                    });
                                            }}
                                            onTimeout={handleNotAuthenticated}
                                            show={showTimeoutModal}
                                        />

                                        <IdleWarningPopup
                                            onExtendSession={async () => {
                                                try {
                                                    await httpClient.refreshToken();
                                                    resetIdleTimer();
                                                    setShowIdleWarningModal(false);
                                                } catch (error) {
                                                    logger.warn('Failed to extend the idle session, logging out', {
                                                        error,
                                                    });
                                                    handleIdleTimeout();
                                                }
                                            }}
                                            onTimeout={handleIdleTimeout}
                                            show={showIdleWarningModal}
                                        />

                                        <div className={classes.contentArea}>
                                            {!shouldHideOnlyNav && (
                                                <div className={classes.routeSidebarOuter}>
                                                    <Navigation onOpenTogglePress={setNavIsOpen} open={navIsOpen} />
                                                </div>
                                            )}

                                            <div
                                                className={classes.contentAreaOverlay}
                                                id="right-side-panel"
                                                onClick={() => {
                                                    dismissAllOverlays();
                                                }}
                                            />

                                            <div className={classes.routeMainOuter} id="routeMainOuterContainer">
                                                {toast.toastMessage.length > 0 && (
                                                    <ToastSuccessMessage
                                                        backgroundColor={toast.backgroundColor}
                                                        iconColor={toast.iconColor}
                                                        iconName={toast.iconName}
                                                        toastData={toast.toastMessage}
                                                    />
                                                )}
                                                <Routes>
                                                    {routes.map((route, index) => {
                                                        if (route.private && route.main) {
                                                            const childPath = route.useChildRouter ? '*' : route.path;
                                                            return (
                                                                <Route
                                                                    element={
                                                                        <PrivateRoute
                                                                            fallback={route.fallback}
                                                                            featureFlag={route.featureFlag}
                                                                            path={route.path}
                                                                            routeGuard={route.routeGuard}
                                                                        />
                                                                    }
                                                                    key={index}
                                                                    path={route.path}
                                                                >
                                                                    <Route element={<route.main />} path={childPath} />;
                                                                </Route>
                                                            );
                                                        }

                                                        if (route.redirectTo) {
                                                            return (
                                                                <Route
                                                                    element={<Navigate to={route.redirectTo} />}
                                                                    key={index}
                                                                    path={route.path}
                                                                />
                                                            );
                                                        }

                                                        if (route.main) {
                                                            return (
                                                                <Route
                                                                    element={<route.main />}
                                                                    key={index}
                                                                    path={route.path}
                                                                />
                                                            );
                                                        }

                                                        return null;
                                                    })}
                                                </Routes>
                                            </div>
                                        </div>
                                    </div>
                                </BmisProvider>
                            </DmsProvider>
                        </CidCvnProvider>
                    </CvtDiagnosticUploadsProvider>
                </CvtProvider>
            </PermissionsProvider>
        </Ssm5ConnectionProvider>
    );
}
